import { useCallback, useEffect, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CheckCircle, ShieldAlert } from 'lucide-react';

interface ResultadoMigracion {
  migrados: number;
  yaEnV2: number;
  ilegibles: string[];
}

/**
 * Botón temporal de SEC-04: migra al cifrado con clave maestra los certificados
 * que se guardaron con el cifrado antiguo. Solo se ve mientras queden
 * pendientes; se elimina con la limpieza final de la migración.
 */
export function MigrarCifrado() {
  const { fetchWithAuth } = useUser();
  const [pendientes, setPendientes] = useState<number | null>(null);
  const [migrando, setMigrando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoMigracion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargarEstado = useCallback(async () => {
    try {
      const respuesta = await fetchWithAuth('/certificados/estado');
      if (respuesta.ok) {
        const datos = await respuesta.json();
        setPendientes(datos.pendientesMigrar);
      }
    } catch (e) {
      console.error('Error al consultar el estado del cifrado:', e);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    cargarEstado();
  }, [cargarEstado]);

  const migrar = async () => {
    setMigrando(true);
    setError(null);
    setResultado(null);
    try {
      const respuesta = await fetchWithAuth('/certificados/migrar', { method: 'POST' });
      const datos = await respuesta.json();
      if (respuesta.ok) {
        setResultado(datos);
      } else {
        setError(datos.error || 'Error al migrar los certificados');
      }
    } catch (e) {
      console.error('Error al migrar certificados:', e);
      setError('Error al migrar los certificados');
    } finally {
      setMigrando(false);
      await cargarEstado();
    }
  };

  const terminado = pendientes === 0 && resultado !== null && resultado.ilegibles.length === 0;
  if (terminado) {
    return (
      <Alert className="mb-6">
        <CheckCircle className="h-4 w-4" />
        <AlertDescription>
          Cifrado migrado: ya no queda ningún certificado con el cifrado antiguo.
        </AlertDescription>
      </Alert>
    );
  }
  if (!pendientes) return null;

  return (
    <Alert className="mb-6">
      <ShieldAlert className="h-4 w-4" />
      <AlertDescription className="space-y-3">
        <p>
          Tienes {pendientes} {pendientes === 1 ? 'certificado' : 'certificados'} con el cifrado antiguo.
          Migra el cifrado para protegerlos con la clave maestra del servidor.
        </p>
        {resultado && resultado.ilegibles.length > 0 && (
          <p className="font-medium">
            {resultado.ilegibles.length === 1 ?
              '1 certificado no se ha podido migrar' :
              `${resultado.ilegibles.length} certificados no se han podido migrar`}
            : sigue con el cifrado antiguo. Si se repite, vuelve a subir su .p12.
          </p>
        )}
        {error && <p className="font-medium">{error}</p>}
        <Button onClick={migrar} disabled={migrando}>
          {migrando ? 'Migrando…' : 'Migrar cifrado'}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
